"use client"

import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom"
import { lazy, Suspense, useEffect } from "react"
import Home from "./pages/Home"

import { Ring } from "ldrs/react"
import "ldrs/react/Ring.css"

// Scroll to top component
function ScrollToTop() {
    const { pathname } = useLocation()

    useEffect(() => {
        // Only scroll to top if there's no hash in the URL
        if (!window.location.hash) {
            window.scrollTo(0, 0)
        }
    }, [pathname])

    return null
}

// Title setter component
function TitleSetter() {
    const { pathname } = useLocation()

    useEffect(() => {
        // Define route to title mapping
        const routeTitles: Record<string, string> = {
            "/": "Accueil",
            "/partners": "Nos Partenaires",
            "/partners/info": "Comment devenir Partenaire",
            "/partners/apply": "Candidature Partenaire",
            "/solutions": "Solutions de Cybersécurité",
            "/services": "Nos Services",
            "/how-it-works": "Comment ça marche",
            "/about": "À propos de S&M",
            "/contact": "Contactez-nous",
            "/legal/privacy-policy": "Politique de Confidentialité",
            "/legal/terms-of-use": "Conditions d'Utilisation",
            "/legal/legal-notice": "Mentions Légales",
        }

        // Get the title for the current path or use a default
        const pageTitle = routeTitles[pathname] || "Page non trouvée"

        // Set the document title with the company name
        document.title = `S&M Cybersécurité | ${pageTitle}`
    }, [pathname])

    return null
}

// Lazy load components for better performance
const PartnersLayout = lazy(() => import("./pages/partners/PartnersLayout"))
const Partners = lazy(() => import("./pages/partners/Partners"))
const PartnerInfo = lazy(() => import("./pages/partners/PartnerInfo"))
const PartnerApplication = lazy(() => import("./pages/partners/PartnerApplication"))

// Main pages
const Solutions = lazy(() => import("./pages/Solutions"))
const Services = lazy(() => import("./pages/Services"))
const HowItWorks = lazy(() => import("./pages/HowItWorks"))
const About = lazy(() => import("./pages/About"))
const Contact = lazy(() => import("./pages/Contact"))

// Legal pages
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"))
const TermsOfUse = lazy(() => import("./pages/legal/TermsOfUse"))
const LegalNotice = lazy(() => import("./pages/legal/LegalNotice"))

// Loading component
const PageLoader = () => (
    <div className="min-h-screen bg-black flex items-center justify-center">
        <Ring size="40" stroke="4" bgOpacity="0" speed="2" color="red" />
    </div>
)

// 404 Page
const NotFound = () => (
    <div className="min-h-screen flex flex-col items-center gap-8 justify-center bg-black text-white">
        <div className="flex flex-row gap-4">
            <p className="font-bold">404</p>
            <span className="border h-8"></span>
            <p>Page non trouvée</p>
        </div>
    </div>
)

function App() {
    return (
        <Router>
            <ScrollToTop />
            <TitleSetter />
            <Suspense fallback={<PageLoader />}>
                <Routes>
                    {/* Home route */}
                    <Route path="/" element={<Home />} />

                    {/* Partners routes with shared layout */}
                    <Route path="/partners" element={<PartnersLayout />}>
                        <Route index element={<Partners />} />
                        <Route path="info" element={<PartnerInfo />} />
                        <Route path="apply" element={<PartnerApplication />} />
                    </Route>

                    {/* Main navigation routes */}
                    <Route path="/solutions" element={<Solutions />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/how-it-works" element={<HowItWorks />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />

                    {/* Legal pages */}
                    <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/legal/terms-of-use" element={<TermsOfUse />} />
                    <Route path="/legal/legal-notice" element={<LegalNotice />} />

                    {/* 404 route */}
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </Router>
    )
}

export default App
